<script setup lang="ts">
// ============================================
// Vue 및 라이브러리 Imports
// ============================================
import { nextTick } from 'vue'
import vSelect from 'vue-select'

// ============================================
// API Imports
// ============================================
import { getMyPassageList, getPassageDetail } from '~/apis/data'
import type { FolderData, PassageData } from '~/apis/data/type'

// ============================================
// Asset Imports
// ============================================
import folderIcon from '~/assets/images/board/img_folder.svg'
import fileIcon from '~/assets/images/board/img_file.svg'

// ============================================
// Utility Imports
// ============================================
import { useCache } from '~/common/useCache'
import { usePassageSelectionStore } from '~/stores/passageSelection'
import { useAlert } from '~/composables/useAlert'
import { PASSAGE_LIMIT } from '~/common/constants'
import { devConsole } from '~/common/devConsole'

// ============================================
// 타입 정의
// ============================================

/**
 * 선택된 지문 타입 정의
 */
interface SelectedPassage {
  title: string
  content: string
  originalContent?: string
  edp_idx?: number
  reg_date?: string
  passage_type?: string
  passage_type_name?: string
  passage_kr?: string | null
  creturn?: 'Y' | 'N'
  creturn_name?: string
}

/**
 * 지문/폴더 아이템 타입 정의
 */
interface PassageItem {
  id: string // 고유 식별자 (passage_123 또는 folder_456)
  type: 'folder' | 'file' // 아이템 타입
  title: string // 제목
  date: string // 생성일자
  passageCount?: number // 폴더인 경우 포함된 지문 수
  originalText?: string // 변형지문인 경우 원본 제목
  label?: string // 라벨 (예: '변형지문')
  folderId?: number // 폴더 ID (폴더인 경우)
  hover_text?: string
}

/**
 * 컴포넌트 이벤트 타입 정의
 */
interface Emits {
  (e: 'search', keyword: string): void
  (e: 'sortChange', option: string): void
  (e: 'itemClick', item: PassageItem): void
  (e: 'pageChange', page: number): void
  (e: 'addSelected', selectedIds: string[]): void
}

// ============================================
// 컴포넌트 설정 및 전역 상태
// ============================================
const emit = defineEmits<Emits>()
const dataLayerPopup = useDataLayerPopup()
const route = useRoute()
const app = useAppStore()

// ============================================
// 상태 관리
// ============================================

// 검색 및 정렬 상태
const searchKeyword = ref('') // 사용자 입력 검색 키워드
const sortOption = ref('date_desc') // 정렬 옵션 (date_desc, date_asc, title_asc)
const isFocused = ref(false)

// 처리 중 상태 관리 (중복 클릭 방지용)
const isProcessing = ref(false)

// v-select용 정렬 옵션
const sortSelectOptions = [
  { label: '생성일시 ↓', value: 'date_desc' },
  { label: '생성일시 ↑', value: 'date_asc' },
  { label: '자료제목↑', value: 'title_asc' },
  { label: '자료제목↓', value: 'title_desc' },
]

// Pinia 스토어를 통한 전역 선택 상태 관리
const passageSelectionStore = usePassageSelectionStore()

// 전역 상태 참조 (computed로 반응성 유지)
const selectedItems = computed({
  get: () => passageSelectionStore.selectedItems,
  set: (value) => {
    passageSelectionStore.selectedItems = value
  },
})

const selectedFolders = computed({
  get: () => passageSelectionStore.selectedFolders,
  set: (value) => {
    passageSelectionStore.selectedFolders = value
  },
})

const folderPassagesCache = computed({
  get: () => passageSelectionStore.folderPassagesCache,
  set: (value) => {
    passageSelectionStore.folderPassagesCache = value
  },
})

// 로컬 참조 관리
const folderCheckboxRefs = ref<Record<string, HTMLInputElement>>({}) // 폴더 체크박스 DOM 참조

// 데이터 상태
const items = ref<PassageItem[]>([]) // 현재 표시 중인 아이템 목록
const totalCount = ref(0) // 전체 지문 개수 (페이징용)
const pageVisible = ref(false) // 페이징 UI 표시 여부

// 페이징 상태
const currentPage = ref(1) // 현재 페이지 번호
const pageSize = ref(10) // 페이지당 아이템 수

// 폴더 네비게이션 상태
const currentFolderId = ref<string>('') // 현재 위치한 폴더 ID
const currentFolderName = ref<string>('') // 현재 위치한 폴더명
const folderHistory = ref<Array<{ id: string; name: string }>>([]) // 폴더 네비게이션 히스토리
const isInFolder = ref(false) // 폴더 내부에 있는지 여부

// 현재 페이지의 전체 선택 여부를 계산 (페이지 단위로 독립적으로 동작)
const isAllSelected = computed(() => {
  const currentPageFileIds = items.value
    .filter((item) => item.type === 'file')
    .map((item) => item.id)

  if (currentPageFileIds.length === 0) return false

  return currentPageFileIds.every((id) =>
    passageSelectionStore.selectedItems.includes(id),
  )
})

// 전체 선택/해제 처리 (현재 페이지만)
const handleSelectAll = (event: Event) => {
  const checked = (event.target as HTMLInputElement).checked

  // 현재 페이지의 파일 아이템만 필터링 (위→아래 순서 보장)
  const currentPageFileIds = items.value
    .filter((item) => item.type === 'file')
    .map((item) => item.id)

  if (checked) {
    // PASSAGE_LIMIT 체크
    const savedData = dataLayerPopup.state.value.savedPassageLoad.data
    const existingPassageIds =
      savedData?.selectedPassages?.map(
        (passage: any) => `passage_${passage.edp_idx}`,
      ) || []

    // 새로 추가될 지문들만 필터링
    const newPassagesFromPage = currentPageFileIds.filter(
      (id) =>
        !existingPassageIds.includes(id) && !selectedItems.value.includes(id),
    )

    const totalAfterSelection =
      getCurrentPassageCount() +
      getNewPassageCount() +
      newPassagesFromPage.length

    if (totalAfterSelection > PASSAGE_LIMIT.MAX_PASSAGES) {
      const alert = useAlert()
      alert.show(PASSAGE_LIMIT.ERROR_MESSAGE)
      // 체크박스 상태 원복
      ;(event.target as HTMLInputElement).checked = false
      return
    }

    // 현재 페이지의 파일들 선택 (순서 보장: 위→아래)
    passageSelectionStore.addItems(currentPageFileIds)
  } else {
    // 현재 페이지의 파일들 선택 해제
    passageSelectionStore.removeItems(currentPageFileIds)
  }
}

// 페이지 변경 감지 및 처리
watch(
  () => currentPage.value,
  async () => {
    await nextTick()
    // 페이지 이동 시 자동 선택 로직 제거 (각 페이지 독립적으로 동작)
  },
)

// ============================================
// Computed Values
// ============================================
const totalPages = computed(() => Math.ceil(totalCount.value / pageSize.value))

/**
 * 특정 아이템의 선택 순서 번호를 반환
 * @param itemId - 아이템 ID
 * @returns 선택 순서 번호 (1-based, 선택되지 않은 경우 0)
 */
const getItemOrderNumber = (itemId: string): number => {
  return passageSelectionStore.getItemSelectionIndex(itemId)
}

/**
 * 경로 기반 카테고리 ID 매핑
 * @description 현재 라우트 경로에 따라 적절한 카테고리 ID를 반환
 * @returns 001: 기본학습자료, 002: 실전유형, 003: 지문변형
 */
const categoryId = computed(() => {
  const path = route.path
  if (path.includes('/data/basic')) {
    return '001' // 기본학습자료
  } else if (path.includes('/data/advanced')) {
    return '002' // 실전유형
  } else if (path.includes('/data/transform')) {
    return '003' // 지문변형
  }
  return '002' // 기본값 (실전유형)
})

/**
 * 페이징 UI에서 표시할 페이지 번호 목록 계산
 * @description 현재 페이지 기준 앞뒤 2페이지씩 최대 5개 페이지 표시
 * @returns 표시할 페이지 번호 배열
 */
const visiblePages = computed(() => {
  const pages: number[] = []
  const start = Math.max(1, currentPage.value - 2)
  const end = Math.min(totalPages.value, start + 4)

  for (let i = start; i <= end; i++) {
    pages.push(i)
  }
  return pages
})

// ============================================
// API 관련 유틸리티 함수
// ============================================

/**
 * 지문 목록을 API로부터 가져오는 메인 함수
 * @description 폴더와 지문 목록을 조회하고 선택 상태를 복원
 * @param folderId - 특정 폴더 ID (없으면 현재 폴더 또는 루트)
 */
const fetchPassageList = async (folderId?: string) => {
  try {
    // API 요청 파라미터 구성
    const sortCol = sortOption.value.startsWith('title_') ? '2' : '1'
    const sortType = sortOption.value.includes('desc') ? 'desc' : 'asc'

    // 지문 목록 API 호출
    const response = await getMyPassageList({
      UM_ID: app.user.UM_ID,
      FOLDER_ID: folderId || currentFolderId.value || '',
      SEARCH_WORD: searchKeyword.value,
      CATEGORY_ID: categoryId.value,
      SORT_COL: sortCol,
      SORT_TYPE: sortType,
      CURRENTPAGE: currentPage.value,
      PAGECOUNT: pageSize.value,
    })

    if (response.data) {
      const { folder, data: passages, totalcount } = response.data

      // 폴더 데이터를 PassageItem 형태로 변환
      const folderItems: PassageItem[] = (folder || []).map((item: any) => ({
        id: `folder_${item.folder_id}`,
        type: 'folder' as const,
        title: item.folder_name,
        date: item.reg_date,
        passageCount: item.passage_cnt,
        folderId: item.folder_id,
      }))

      // 지문 데이터를 PassageItem 형태로 변환
      const passageItems: PassageItem[] = (passages || []).map((item: any) => ({
        id: `passage_${item.edp_idx}`,
        type: 'file' as const,
        title: item.edp_title,
        date: item.reg_date,
        label: item.passage_type === '2' ? item.passage_type_name : undefined,
        originalText:
          item.passage_type === '2'
            ? item.orginal_passage_title || '원문 정보 없음'
            : undefined,
        hover_text: item.hover_text,
      }))

      // UI에 표시할 아이템 목록 구성 (폴더 + 지문)
      items.value = [...folderItems, ...passageItems]
      totalCount.value = totalcount || 0

      // 선택 상태는 Pinia persist 및 사용자 액션으로만 관리
      // fetchPassageList()는 목록 조회만 담당, 선택 상태 건드리지 않음

      // 폴더 선택 상태 업데이트 (전역 상태 기반)
      selectedFolders.value.forEach((folderId) => {
        passageSelectionStore.updateFolderSelectionState(folderId)
      })

      // 폴더 선택 상태 복원 및 페이징 UI 표시 여부 설정
      await restoreFolderSelections()
      pageVisible.value = totalCount.value > pageSize.value
    }
  } catch (error) {
    devConsole.error('Failed to fetch passage list:', error)
  } finally {
    isFocused.value = searchKeyword.value !== ''
  }
}

// ============================================
// 폴더 관련 유틸리티 함수
// ============================================

/**
 * 팝업 재오픈 시 폴더 선택 상태를 복원하는 함수
 * @description 이전에 선택했던 폴더들을 다시 선택 상태로 표시
 */
const restoreFolderSelections = async () => {
  const savedPassageLoadData = dataLayerPopup.state.value.savedPassageLoad.data
  if (!savedPassageLoadData?.selectedPassages) return

  const selectedPassageIds = savedPassageLoadData.selectedPassages
    .filter((passage: any) => passage.edp_idx)
    .map((passage: any) => `passage_${passage.edp_idx}`)

  const currentFolders = items.value.filter((item) => item.type === 'folder')

  for (const folder of currentFolders) {
    const folderId = folder.folderId?.toString() || ''
    await fetchFolderPassagesForCheck(folderId)

    // 폴더의 모든 지문이 선택되었을 때만 selectedFolders에 추가
    const folderPassageIds = folderPassagesCache.value[folderId] || []
    const selectedCount = folderPassageIds.filter((id) =>
      selectedItems.value.includes(id),
    ).length

    if (
      selectedCount === folderPassageIds.length &&
      folderPassageIds.length > 0
    ) {
      if (!selectedFolders.value.includes(folderId)) {
        selectedFolders.value.push(folderId)
      }
    }
  }

  // 상태 복원 후 체크박스 업데이트
  nextTick(() => {
    updateFolderCheckboxStates()
  })
}

// 체크박스 상태 업데이트 함수
const updateFolderCheckboxStates = () => {
  items.value
    .filter((item) => item.type === 'folder')
    .forEach((folder) => {
      const folderId = folder.folderId?.toString() || ''
      const checkbox = folderCheckboxRefs.value[folderId]

      if (checkbox) {
        const state = getFolderSelectionState(folderId)
        checkbox.checked = state === 'selected'
      }
    })
}

// ============================================
// 이벤트 핸들러
// ============================================

/**
 * 레이어 팝업을 닫는 핸들러 (X 버튼 또는 ESC 키)
 * @description 선택 상태 완전 초기화 및 폴더 네비게이션 리셋
 */
const handleCloseLayer = () => {
  // 폴더 네비게이션 상태를 루트로 초기화
  currentFolderId.value = ''
  currentFolderName.value = ''
  isInFolder.value = false
  folderHistory.value = []

  // 선택 상태 완전 초기화 (페이지별 독립적인 선택 상태 유지를 위해)
  passageSelectionStore.resetAll()

  dataLayerPopup.hideSavedPassageLoad()
}

// ESC 키 이벤트 핸들러
const handleEscapeKey = (event: KeyboardEvent) => {
  if (event.key === 'Escape') {
    handleCloseLayer()
  }
}

const handleSearchInputChange = () => {
  // 실시간 검색 로직은 debounce 처리가 필요할 수 있음
}

const handleSearchButtonClick = () => {
  currentPage.value = 1
  fetchPassageList()
  emit('search', searchKeyword.value)
}

const handleSortOptionChange = () => {
  currentPage.value = 1
  fetchPassageList()
  emit('sortChange', sortOption.value)
}

// 폴더 내비게이션 함수 (히스토리 관리)
const navigateToFolder = async (folderId: string, folderName: string) => {
  // 현재 위치를 히스토리에 추가
  if (!isInFolder.value) {
    folderHistory.value.push({ id: '', name: '저장된 지문 불러오기' })
  } else {
    folderHistory.value.push({
      id: currentFolderId.value,
      name: currentFolderName.value,
    })
  }

  currentFolderId.value = folderId
  currentFolderName.value = folderName
  isInFolder.value = true
  currentPage.value = 1

  // 폴더 데이터를 가져오기 (선택 없이 순수 네비게이션만 수행)
  await fetchPassageList(folderId)
}

// 루트로 돌아가기 (선택 유지)
const navigateToRoot = () => {
  currentFolderId.value = ''
  currentFolderName.value = ''
  isInFolder.value = false
  folderHistory.value = []
  currentPage.value = 1
  // 루트로 이동 시에도 선택 항목 유지
  fetchPassageList('')
}

// 이전 폴더로 돌아가기
const navigateBack = () => {
  if (folderHistory.value.length === 0) return

  const previousLocation = folderHistory.value.pop()
  if (!previousLocation) return

  if (previousLocation.id === '') {
    // 루트로 돌아가기
    navigateToRoot()
  } else {
    // 이전 폴더로 돌아가기
    currentFolderId.value = previousLocation.id
    currentFolderName.value = previousLocation.name
    currentPage.value = 1

    if (folderHistory.value.length === 0) {
      isInFolder.value = false
    }

    fetchPassageList(previousLocation.id)
  }
}

// 폴더 더블클릭 이벤트 처리 (폴더로 이동) - 싱글클릭과 동일한 동작
const handleFolderDoubleClick = (item: PassageItem) => {
  // 더블클릭 시에는 추가 동작 없음 (싱글클릭으로 이미 처리됨)
  // 필요시 여기에 추가 로직 구현 가능
}

// 폴더 텍스트 클릭 이벤트 처리 (폴더로 이동)
const handleFolderTextClick = (item: PassageItem) => {
  if (item.type === 'folder') {
    navigateToFolder(item.folderId?.toString() || '', item.title)
  }
}

const handleItemTitleClick = (item: PassageItem) => {
  if (item.type === 'file') {
    const isCurrentlySelected = selectedItems.value.includes(item.id)

    if (!isCurrentlySelected) {
      // 이미 페이지에 있는 지문인지 확인
      const savedData = dataLayerPopup.state.value.savedPassageLoad.data
      const existingPassageIds =
        savedData?.selectedPassages?.map(
          (passage: any) => `passage_${passage.edp_idx}`,
        ) || []

      const isAlreadyInPage = existingPassageIds.includes(item.id)

      // 페이지에 없는 새로운 지문인 경우에만 제한 체크
      if (!isAlreadyInPage) {
        const totalAfterSelection = getTotalPassageCount() + 1

        if (totalAfterSelection > PASSAGE_LIMIT.MAX_PASSAGES) {
          const alert = useAlert()
          alert.show(PASSAGE_LIMIT.ERROR_MESSAGE)
          return
        }
      }
    }

    // 지문 제목 클릭 시 체크박스 선택 토글 (스토어 사용)
    passageSelectionStore.toggleItem(item.id)

    // 선택 상태 변경 후 UI 업데이트
    handleItemSelectionChange()

    // 기존 이벤트도 유지
    emit('itemClick', item)
  }
}

const handleItemSelectionChange = () => {
  // 체크박스 변경 시 selectionOrder 동기화
  const currentSelectionOrder = passageSelectionStore.selectionOrder
  const newSelectedItems = selectedItems.value

  // 새로 추가된 아이템 찾기
  const addedItems = newSelectedItems.filter(
    (id) => !currentSelectionOrder.includes(id),
  )

  // 제거된 아이템 찾기
  const removedItems = currentSelectionOrder.filter(
    (id) => !newSelectedItems.includes(id),
  )

  // selectionOrder 업데이트
  if (addedItems.length > 0) {
    passageSelectionStore.selectionOrder.push(...addedItems)
  }
  if (removedItems.length > 0) {
    passageSelectionStore.selectionOrder =
      passageSelectionStore.selectionOrder.filter(
        (id) => !removedItems.includes(id),
      )
  }

  // 실제 총 개수 검증 (페이지 지문 + 새로 추가할 지문)
  const totalCount = getTotalPassageCount()

  if (totalCount > PASSAGE_LIMIT.MAX_PASSAGES) {
    const alert = useAlert()
    alert.show(PASSAGE_LIMIT.ERROR_MESSAGE)

    // 마지막 선택 항목 제거 (v-model이 이미 추가했으므로)
    const lastItem = selectedItems.value[selectedItems.value.length - 1]
    const index = selectedItems.value.indexOf(lastItem)
    if (index > -1) {
      selectedItems.value.splice(index, 1)
      // selectionOrder에서도 제거
      const orderIndex = passageSelectionStore.selectionOrder.indexOf(lastItem)
      if (orderIndex > -1) {
        passageSelectionStore.selectionOrder.splice(orderIndex, 1)
      }
    }
    return
  }

  // selectedItems가 reactive하므로 자동으로 업데이트됨
  // 개별 지문 선택 시 폴더 체크박스 상태도 업데이트
  nextTick(() => {
    updateFolderCheckboxStates()
  })
}

// 폴더 체크박스 ref 설정 함수
const setFolderCheckboxRef = (
  folderId: string,
  el: HTMLInputElement | null,
) => {
  if (el) {
    folderCheckboxRefs.value[folderId] = el
  }
}

// 폴더 선택 상태 계산 함수 (스토어 getter 사용)
const getFolderSelectionState = (folderId: string) => {
  return passageSelectionStore.getFolderSelectionState(folderId)
}

// 폴더 지문 가져오기 및 선택 함수 (스토어 사용)
const fetchAndSelectFolderPassages = async (folderId: string) => {
  try {
    // 캐시에 있으면 캐시 사용
    if (folderPassagesCache.value[folderId]) {
      const cachedPassages = folderPassagesCache.value[folderId]
      passageSelectionStore.addItems(cachedPassages)
      return
    }

    // API 호출
    const response = await getMyPassageList({
      UM_ID: app.user.UM_ID,
      FOLDER_ID: folderId,
      SEARCH_WORD: '',
      CATEGORY_ID: categoryId.value,
      SORT_COL: '1',
      SORT_TYPE: 'desc',
      CURRENTPAGE: 1,
      PAGECOUNT: 9999, // 모든 지문 가져오기
    })

    // 지문 ID들을 캐시에 저장하고 선택 배열에 추가
    const passageIds = response.data.data.map(
      (item: any) => `passage_${item.edp_idx}`,
    )
    passageSelectionStore.updateFolderCache(folderId, passageIds)
    passageSelectionStore.addItems(passageIds)
  } catch (error) {
    devConsole.error('Failed to fetch folder passages:', error)
  }
}

// 폴더 지문 선택 해제 함수 (스토어 사용)
const removeFolderPassagesFromSelection = (folderId: string) => {
  const folderPassageIds = folderPassagesCache.value[folderId] || []
  passageSelectionStore.removeItems(folderPassageIds)
}

// 폴더 지문 확인용 함수 (선택 처리 없이 캐시만)
const fetchFolderPassagesForCheck = async (folderId: string) => {
  if (folderPassagesCache.value[folderId]) return // 이미 캐시에 있음

  try {
    const response = await getMyPassageList({
      UM_ID: app.user.UM_ID,
      FOLDER_ID: folderId,
      SEARCH_WORD: '',
      CATEGORY_ID: categoryId.value,
      SORT_COL: '1',
      SORT_TYPE: 'desc',
      CURRENTPAGE: 1,
      PAGECOUNT: 9999,
    })

    const passageIds = response.data.data.map(
      (item: any) => `passage_${item.edp_idx}`,
    )
    passageSelectionStore.updateFolderCache(folderId, passageIds)
  } catch (error) {
    devConsole.error('Failed to fetch folder passages for check:', error)
  }
}

// 폴더 체크박스 이벤트 핸들러 (스토어 사용)
const handleFolderSelectionChange = async (
  folderId: string,
  isChecked: boolean,
) => {
  if (isChecked) {
    // 폴더 선택 전 5개 제한 검증
    // 먼저 폴더 내 지문 수 확인
    await fetchFolderPassagesForCheck(folderId)
    const folderPassageIds = folderPassagesCache.value[folderId] || []

    // 이미 페이지나 팝업에서 선택된 지문들 제외
    const savedData = dataLayerPopup.state.value.savedPassageLoad.data
    const existingPassageIds =
      savedData?.selectedPassages?.map(
        (passage: any) => `passage_${passage.edp_idx}`,
      ) || []

    // 폴더의 지문 중 실제로 새로 추가될 지문들만 계산
    const newPassagesFromFolder = folderPassageIds.filter(
      (id) =>
        !existingPassageIds.includes(id) && !selectedItems.value.includes(id),
    )

    const totalAfterFolderSelection =
      getCurrentPassageCount() +
      getNewPassageCount() +
      newPassagesFromFolder.length

    if (totalAfterFolderSelection > PASSAGE_LIMIT.MAX_PASSAGES) {
      const alert = useAlert()
      alert.show(PASSAGE_LIMIT.ERROR_MESSAGE)

      // 체크박스 상태 원복
      const checkbox = folderCheckboxRefs.value[folderId]
      if (checkbox) {
        checkbox.checked = false
      }
      return
    }

    // 폴더 선택
    await fetchAndSelectFolderPassages(folderId)
    passageSelectionStore.selectFolder(folderId, folderPassageIds)
  } else {
    // 폴더 선택 해제
    passageSelectionStore.deselectFolder(folderId)
  }

  // 체크박스 상태 업데이트
  nextTick(() => {
    updateFolderCheckboxStates()
  })
}

const handlePreviousPageClick = () => {
  if (currentPage.value > 1) {
    currentPage.value--
    fetchPassageList()
    emit('pageChange', currentPage.value)
  }
}

const handleNextPageClick = () => {
  if (currentPage.value < totalPages.value) {
    currentPage.value++
    fetchPassageList()
    emit('pageChange', currentPage.value)
  }
}

const handlePageClick = (page: number) => {
  currentPage.value = page
  fetchPassageList()
  emit('pageChange', page)
}

/**
 * 현재 추가된 지문 개수 확인 함수
 * @returns 현재 추가되어 있는 지문 개수
 */
const getCurrentPassageCount = (): number => {
  const savedData = dataLayerPopup.state.value.savedPassageLoad.data
  return savedData?.selectedPassages?.length || 0
}

/**
 * 팝업에서 선택하려는 새로운 지문들의 개수를 계산
 * (이미 페이지에 있는 지문들은 제외)
 * @returns 새로 추가될 지문 개수
 */
const getNewPassageCount = (): number => {
  const savedData = dataLayerPopup.state.value.savedPassageLoad.data
  const existingPassageIds =
    savedData?.selectedPassages?.map(
      (passage: any) => `passage_${passage.edp_idx}`,
    ) || []

  // 팝업에서 선택한 지문 중 페이지에 없는 새로운 지문들만 계산
  return selectedItems.value
    .filter((id) => id.startsWith('passage_'))
    .filter((id) => !existingPassageIds.includes(id)).length
}

/**
 * 현재 총 지문 개수 계산 (페이지 + 새로 선택할 지문)
 * @returns 총 지문 개수
 */
const getTotalPassageCount = (): number => {
  return getCurrentPassageCount() + getNewPassageCount()
}

/**
 * 선택된 지문들을 추가하는 메인 핸들러
 * @description 선택된 지문들의 상세 정보를 가져와서 콜백으로 전달
 */
const handleAddSelectedItemsClick = async () => {
  // 이미 처리 중이면 무시 (중복 클릭 방지)
  if (isProcessing.value) return

  if (selectedItems.value.length === 0) return

  isProcessing.value = true

  try {
    const callback = dataLayerPopup.state.value.savedPassageLoad.callback
    if (callback) {
      // 이미 선택된 지문들의 ID 가져오기
      const savedPassageLoadData =
        dataLayerPopup.state.value.savedPassageLoad.data
      const existingPassageIds =
        savedPassageLoadData?.selectedPassages?.map(
          (passage: any) => `passage_${passage.edp_idx}`,
        ) || []

      // 현재 팝업에서 선택된 모든 지문 ID
      const allSelectedPassageIds = selectedItems.value.filter((itemId) =>
        itemId.startsWith('passage_'),
      )

      // 제거된 지문 ID 계산 (기존에는 있었지만 현재 선택에서는 없는 지문)
      const removedPassageIds = existingPassageIds
        .filter((id: string) => !allSelectedPassageIds.includes(id))
        .map((id: string) => parseInt(id.replace('passage_', '')))

      // 선택된 지문 ID들만 필터링 (폴더로 선택된 지문들도 포함, 이미 선택된 지문은 제외)
      const validPassageIds = selectedItems.value
        .filter((itemId) => !existingPassageIds.includes(itemId))
        .filter((itemId) => itemId.startsWith('passage_'))

      // 5개 제한 검증 (이미 필터링된 validPassageIds 사용)
      const currentCount = getCurrentPassageCount()
      if (currentCount + validPassageIds.length > PASSAGE_LIMIT.MAX_PASSAGES) {
        const alert = useAlert()
        alert.show(PASSAGE_LIMIT.ERROR_MESSAGE)
        isProcessing.value = false
        return
      }

      // 선택 순서 매칭 기능: selectionOrder 순서로 전체 배열 재구성
      // 선택된 항목이 있으면 항상 callback 호출 (순서만 변경된 경우도 처리)
      if (allSelectedPassageIds.length > 0 || removedPassageIds.length > 0) {
        // 1. 기존 지문 맵 생성 (페이지에서 전달받은 지문들)
        const passageMap = new Map<string, SelectedPassage>()
        savedPassageLoadData?.selectedPassages?.forEach(
          (p: SelectedPassage) => {
            passageMap.set(`passage_${p.edp_idx}`, p)
          },
        )

        // 2. 신규 지문 API 호출 후 맵에 추가
        if (validPassageIds.length > 0) {
          // 각 지문의 상세 정보를 API로 가져오기
          const passagePromises = validPassageIds.map(async (itemId) => {
            const edpIdx = itemId.replace('passage_', '')
            const response = await getPassageDetail({
              UM_ID: app.user.UM_ID,
              EDP_IDX: edpIdx,
            })

            // 모든 지문 정보를 포함해서 반환
            const originalContent = response.data.data.passage
            const processedContent =
              response.data.data.creturn === 'N'
                ? originalContent.replace(/\n/g, ' ')
                : originalContent

            return {
              title: response.data.data.edp_title,
              content: processedContent,
              originalContent: originalContent,
              edp_idx: response.data.data.edp_idx,
              reg_date: response.data.data.reg_date,
              passage_type: response.data.data.passage_type,
              passage_type_name: response.data.data.passage_type_name,
              passage_kr: response.data.data.passage_kr,
              creturn: response.data.data.creturn,
              creturn_name: response.data.data.creturn_name,
            }
          })

          const newPassages = await Promise.all(passagePromises)
          newPassages.forEach((p) => {
            passageMap.set(`passage_${p.edp_idx}`, p)
          })
        }

        // 3. selectionOrder 순서로 전체 배열 생성 (정렬된 전체 상태)
        const orderedPassages = passageSelectionStore.selectionOrder
          .map((id) => passageMap.get(id))
          .filter(Boolean)

        // 4. 콜백 호출 (정렬된 전체 배열 전달, removedPassageIds는 빈 배열)
        callback({
          passages: orderedPassages,
          removedPassageIds: [], // 이미 passages에 반영됨
        })
      }
    }
    emit('addSelected', selectedItems.value)
    // 선택 완료 후 스토어 초기화
    passageSelectionStore.resetAll()
    dataLayerPopup.hideSavedPassageLoad()
  } catch (error) {
    devConsole.error('Failed to fetch passage details:', error)
    // 에러 발생 시에도 UI를 복구하기 위해 기본 처리
    const callback = dataLayerPopup.state.value.savedPassageLoad.callback
    if (callback) {
      const validPassageIds = selectedItems.value.filter((itemId) =>
        itemId.startsWith('passage_'),
      )

      const passages = validPassageIds.map((itemId) => ({
        title: `지문 ${itemId.replace('passage_', '')}`,
        content: `지문 내용을 불러올 수 없습니다.`,
        originalContent: `지문 내용을 불러올 수 없습니다.`,
      }))
      callback(passages)
    }
    passageSelectionStore.resetAll()
    dataLayerPopup.hideSavedPassageLoad()
  } finally {
    // 처리 완료 후 플래그 해제
    isProcessing.value = false
  }
}

// ============================================
// 라이프사이클 & 이벤트 리스너 (스크립트 하단)
// ============================================

/**
 * 컴포넌트 마운트 시 실행되는 초기화 로직
 */
onMounted(() => {
  // 폴더 네비게이션 상태를 루트로 초기화 (매번 루트에서 시작)
  currentFolderId.value = ''
  currentFolderName.value = ''
  isInFolder.value = false
  folderHistory.value = []

  // 선택 상태 완전 초기화 (이전 페이지/팝업의 상태 제거)
  passageSelectionStore.resetAll()

  // 페이지에서 전달받은 selectedPassages 기반 초기화
  const savedPassageLoadData = dataLayerPopup.state.value.savedPassageLoad.data
  if (savedPassageLoadData?.selectedPassages) {
    const selectedPassageIds = savedPassageLoadData.selectedPassages
      .filter((passage: any) => passage.edp_idx)
      .map((passage: any) => `passage_${passage.edp_idx}`)

    // 현재 페이지의 지문들만 선택 상태로 설정 + 순서 복원
    if (selectedPassageIds.length > 0) {
      // 팝업 재오픈 시 페이지의 지문 순서를 selectionOrder에 복원
      passageSelectionStore.selectionOrder = [...selectedPassageIds]
      passageSelectionStore.addItems(selectedPassageIds)
    }
  }

  // 초기 지문 목록 로드
  fetchPassageList()

  // ESC 키 이벤트 리스너 등록
  document.addEventListener('keydown', handleEscapeKey)
})

/**
 * 컴포넌트 언마운트 시 정리 작업
 */
onUnmounted(() => {
  // 선택 상태 완전 초기화
  passageSelectionStore.resetAll()

  // 이벤트 리스너 해제
  document.removeEventListener('keydown', handleEscapeKey)
})

watch(
  () => searchKeyword.value,
  (value) => {
    if (value === '') {
      isFocused.value = false
      handleSearchButtonClick()
    }
  },
)
</script>
<template>
  <div class="layer active">
    <div class="mask" @click="handleCloseLayer"></div>
    <div class="inner">
      <section
        :class="['sample passageLoadLayer', { fullHeight: items.length > 0 }]"
      >
        <header>
          <h2 v-if="!isInFolder">저장된 지문 불러오기</h2>
          <template v-else>
            <a class="layerBack" @click="navigateBack">뒤로</a>
            <h2>{{ currentFolderName }}</h2>
          </template>
          <a class="layerClose" @click="handleCloseLayer">닫기</a>
        </header>
        <main>
          <!--<main :style="{ overflow: items.length === 0 ? 'hidden' : 'auto' }">-->
          <div class="searchArea">
            <div class="search">
              <input
                type="text"
                placeholder="자료 제목 검색"
                v-model="searchKeyword"
                @input="handleSearchInputChange"
                @keydown.enter="handleSearchButtonClick"
              />
              <a
                v-if="!isFocused"
                class="btnSearch"
                @click="handleSearchButtonClick"
              >
                검색
              </a>
              <a v-else class="btnSearchClear" @click="searchKeyword = ''">
                검색 내용 지우기
              </a>
            </div>
            <ClientOnly>
              <v-select
                :options="sortSelectOptions"
                v-model="sortOption"
                label="label"
                :reduce="(option) => option.value"
                @update:modelValue="handleSortOptionChange"
                :clearable="false"
                :searchable="false"
              >
                <template #no-options> 검색 결과 없음 </template>
              </v-select>
            </ClientOnly>
          </div>
          <div class="popDataNone" v-if="items.length === 0">
            <p>자료가 없습니다</p>
          </div>
          <div v-if="items.length !== 0" class="passageCheckAll">
            <label class="label">
              <input
                type="checkbox"
                class="selectAllCheckbox"
                :checked="isAllSelected"
                @change="handleSelectAll"
              />
              <span>전체 선택 ({{ totalCount ?? '0' }})</span>
            </label>
          </div>
          <div class="passageLoadList" v-if="items.length > 0">
            <ul>
              <li v-for="item in items" :key="item.id">
                <label
                  class="label"
                  @click="
                    item.type === 'folder' ? handleFolderTextClick(item) : null
                  "
                  @dblclick.stop="
                    item.type === 'folder'
                      ? handleFolderDoubleClick(item)
                      : null
                  "
                >
                  <span v-if="item.type === 'folder'" class="img">
                    <img
                      :src="item.type === 'folder' ? folderIcon : fileIcon"
                      :alt="item.type === 'folder' ? '폴더' : '파일'"
                    />
                  </span>
                  <!-- 파일용 체크박스 -->
                  <input
                    type="checkbox"
                    v-if="item.type === 'file'"
                    v-model="selectedItems"
                    :value="item.id"
                    @change="handleItemSelectionChange"
                  />
                  <span
                    v-if="
                      item.type === 'file' && getItemOrderNumber(item.id) > 0
                    "
                    class="num"
                  >
                    {{ getItemOrderNumber(item.id) }}
                  </span>
                  <!-- 폴더용 체크박스 -->
                  <!--
                  <input
                      type="checkbox"
                      class="hidden-checkbox"
                      v-if="item.type === 'folder'"
                      :ref="
                      (el) =>
                        setFolderCheckboxRef(
                          item.folderId?.toString() || '',
                          el as HTMLInputElement,
                        )
                    "
                      :checked="
                      getFolderSelectionState(
                        item.folderId?.toString() || '',
                      ) === 'selected'
                    "
                      @change="
                      handleFolderSelectionChange(
                        item.folderId?.toString() || '',
                        ($event.target as HTMLInputElement).checked,
                      )
                    "
                  />
                  -->
                  <span class="wrap">
                    <span class="tit">
                      {{ item.title }}
                      <span v-if="item.label" class="spanlabel">{{
                        item.label
                      }}</span>
                    </span>
                    <span class="ul">
                      <span class="li">{{ item.date }}</span>
                      <span class="li" v-if="item.type === 'folder'">
                        지문 {{ item.passageCount }}개
                      </span>
                      <span class="li" v-else-if="item.originalText">
                        원문 : {{ item.originalText }}
                      </span>
                    </span>
                  </span>
                  <div v-if="item.type !== 'folder'" class="tooltip">
                    <div>
                      {{ item.hover_text }}
                    </div>
                  </div>
                </label>
              </li>
            </ul>
          </div>

          <div class="pagingArea" v-if="pageVisible">
            <ul>
              <li>
                <a class="btnPrev" @click="handlePreviousPageClick">이전</a>
              </li>
              <li v-for="page in visiblePages" :key="page">
                <strong v-if="page === currentPage">{{ page }}</strong>
                <a v-else @click="handlePageClick(page)">{{ page }}</a>
              </li>
              <li><a class="btnNext" @click="handleNextPageClick">다음</a></li>
            </ul>
          </div>
        </main>
        <footer>
          <div class="btnWrap">
            <a
              class="btnStyle wide"
              :class="{ disable: selectedItems.length === 0 || isProcessing }"
              @click="handleAddSelectedItemsClick"
            >
              {{
                selectedItems.length === 0
                  ? '지문을 선택해주세요'
                  : `${selectedItems.length}개 추가하기`
              }}
            </a>
          </div>
        </footer>
      </section>
    </div>
  </div>
</template>
<style scoped>
/*.hidden-checkbox {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0,0,0,0);
  border: 0;
}*/
</style>
